import { createContext, useContext, useEffect, useReducer } from 'react';

import { useGlobalState } from './GlobalState';
import { AuthContext } from './AuthContext';
import { useLoadingState } from '../hooks/useLoader';
import { fetchGreenpillCount } from '../apis/geenpills/numberOfGreenPills';
import { getWalletNFTs } from '../apis/geenpills/nftsHoldByWalletAddress';
import { getPrimaryPill, setPrimaryPill } from '../apis/geenpills/getAndSetPrimaryPill';
import { getPillProgress } from '../apis/geenpills/getPillProgress';

const HomeContext = createContext();
const getInitialActiveCollection = () => {
    const stored = localStorage.getItem('activeCollection');
    try {
        return stored ? JSON.parse(stored) : 0;
    } catch {
        return null;
    }
};
const initialState = {
    activeCollection: getInitialActiveCollection(),
    greenPillNFTsHoldByWalletAddress: [],
    greenPillNFTsCountInLandscapes: [],
    selectedPill: null,
    userStats: null,
};

function reducer(state, action) {
    switch (action.type) {
        case 'home/setHomeDetails':
            return {
                ...state,
                greenPillNFTsHoldByWalletAddress: action.payload?.walletNFTs,
            };
        case 'home/setGreenpillCounts':
            return {
                ...state,
                greenPillNFTsCountInLandscapes: action.payload?.landscapesGreenCount,
            };
        case 'home/setScriptInfos':
            return { ...state };

        case 'home/setSelectedPill':
            return { ...state, selectedPill: action.payload };

        case 'home/setUserSpecificStats':
            return { ...state, userStats: action.payload };

        case 'home/setActiveCollection':
            const updatedCollection = action.payload;
            localStorage.setItem('activeCollection', JSON.stringify(updatedCollection));
            return { ...state, activeCollection: updatedCollection };
        // return { ...state, activeCollection: action.payload }

        default:
            return state;
    }
}

const HomeProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { state: landscapesState } = useGlobalState();
    const { state: authState } = useContext(AuthContext);
    const account = authState?.user[0]?.evmAddress;
    const solanaAccount = authState?.user[0]?.walletAddress;

    // const { account } = useAccount()
    const { loading, startLoading, stopLoading } = useLoadingState();
    const {
        loading: gettingGreenpillCounts,
        startLoading: startGettingGreenpillCounts,
        stopLoading: stopGettingGreenpillCounts,
    } = useLoadingState();
    const fetchNFT = async (
        chainId,
        contractAddress,
        nftCollectionNumber,
        chainName,
        collectionAddress,
        name,
        chainImage,
        landscapeId
    ) => {
        try {
            const response = await getWalletNFTs(
                contractAddress,
                chainName !== 'solana' ? account : solanaAccount,
                chainId,
                nftCollectionNumber,
                chainName,
                collectionAddress,
                name,
                chainImage,
                landscapeId
            );

            return response;
        } catch (error) {
            return null;
        }
    };

    const fetchGreenCount = async (
        chainId,
        contractAddress,
        stateURIs,
        nftCollectionNumber,
        chainName,
        collectionAddress
    ) => {
        try {
            const response = await fetchGreenpillCount(
                chainId,
                contractAddress,
                stateURIs,
                nftCollectionNumber,
                chainName,
                collectionAddress
            );

            return response;
        } catch (error) {
            return null;
        }
    };

    const fetchCounts = async () => {
        try {
            startGettingGreenpillCounts();

            // const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

            const landscapes = landscapesState?.landscapes || [];

            const aggregatedResults = [];

            for (let landscape of landscapes) {
                const {
                    contractAddresses,
                    stateURIs,
                    shockStateURIs,
                    stateURIsForSolana,
                    shockStateURIsForSolana,
                    nftCollectionNumber,
                } = landscape;

                const calls = contractAddresses
                    .filter(({ chainId }) => chainId !== 11155420)
                    .map(({ chainId, contractAddress, chainName, collectionAddress }) => {
                        const uris =
                            chainName === 'solana'
                                ? [
                                      stateURIsForSolana ? stateURIsForSolana?.[4] : '',
                                      ...(shockStateURIsForSolana ?? []),
                                  ]
                                : [stateURIs?.[4], ...(shockStateURIs ?? [])];

                        return () =>
                            fetchGreenCount(
                                chainId,
                                contractAddress,
                                uris,
                                nftCollectionNumber,
                                chainName,
                                collectionAddress
                            );
                    });

                let landscapeTotal = 0;

                // Sequentially call each fetchGreenCount for current landscape
                for (let call of calls) {
                    try {
                        const res = await call();
                        landscapeTotal += res?.count ?? 0;
                    } catch (err) {
                        console.error(`Error in collection ${nftCollectionNumber}:`, err);
                    }
                    // await delay(300) // Add delay between calls
                }

                aggregatedResults.push({
                    count: landscapeTotal,
                    landscapeCollection: nftCollectionNumber,
                });
            }

            const countsFetched = aggregatedResults;

            dispatch({
                type: 'home/setGreenpillCounts',
                payload: {
                    landscapesGreenCount: countsFetched,
                },
            });
        } catch (error) {
            console.error(error, 'erroe');
        } finally {
            stopGettingGreenpillCounts();
        }
    };

    const fetchData = async () => {
        try {
            startLoading();

            const landscapes = landscapesState?.landscapes || [];

            const aggregatedNFTs = [];
            const timeStart = new Date()?.getTime();
            for (let landscape of landscapes) {
                const { contractAddresses, nftCollectionNumber, name, _id } = landscape;

                const results = await Promise.all(
                    contractAddresses.map(({ chainId, contractAddress, chainName, collectionAddress, chainImage }) =>
                        fetchNFT(
                            chainId,
                            contractAddress,
                            nftCollectionNumber,
                            chainName,
                            collectionAddress,
                            name,
                            chainImage,
                            _id
                        )
                    )
                );

                aggregatedNFTs.push(...results.flat());

                // const calls = contractAddresses.map(
                //   ({
                //     chainId,
                //     contractAddress,
                //     chainName,
                //     collectionAddress,
                //     chainImage,
                //   }) =>
                //     fetchNFT(
                //       chainId,
                //       contractAddress,
                //       nftCollectionNumber,
                //       chainName,
                //       collectionAddress,
                //       name,
                //       chainImage
                //     )
                // )

                // const results = await Promise.all(calls)

                // for (const res of results) {
                //   if (Array.isArray(res)) {
                //     aggregatedNFTs.push(...res)
                //   }
                // }
            }
            const timeEnd = new Date()?.getTime();

            dispatch({
                type: 'home/setHomeDetails',
                payload: {
                    walletNFTs: aggregatedNFTs,
                },
            });
        } catch (error) {
            console.error(error, 'error');
        } finally {
            stopLoading();
        }
    };

    const fetchSelectedPill = async () => {
        try {
            const response = await getPrimaryPill();

            const found = state?.greenPillNFTsHoldByWalletAddress?.find(
                ({ chainName, contractAddress, collectionAddress, tokenId }) => {
                    return (
                        chainName + (chainName === 'solana' ? collectionAddress : contractAddress) + tokenId ===
                        response?.chainName +
                            (response?.chainName === 'solana'
                                ? response?.collectionAddress
                                : response?.contractAddress) +
                            (response?.chainName === 'solana' ? response?.assetId : response?.heldTokenId)
                    );
                }
            );
            console.log(found, state?.greenPillNFTsHoldByWalletAddress, 'see found pill');

            // if (response?.id && !found) {
            //     const primaryPillTransferred = await getPillProgress(response?.id);
            //     console.log(primaryPillTransferred, 'see is nft id coming or not', 'transferred');

            //     // const pill = {
            //     //     impactPointsEarned: 652.3,
            //     //     lock: false,
            //     //     landscapeId: '67f443e072efff145ea6f4a7',
            //     //     chainName: 'solana',
            //     //     collectionAddress: 'Xf5AwV2AJnKviKt54Ny5iMEg8ttshhNopUi3sJ4Woyc',
            //     //     assetId: '7XhvYDuFiFQyDyFAfon44YazEA2gUPD3TDFW8N8BNPEj',
            //     //     uid: 't4zKxQpj0dMWjSn2p4CGfRCYj663',
            //     //     walletAddress: 'A1wyiT6J2yxFgWvcAorGC3dBmb7yzUkwn49LUww6SvvQ',
            //     //     platform: 'IMPACT_FOUNDRY',
            //     //     active: true,
            //     //     id: '684349d443515508ee14a740',
            //     // };

            //     // const pill3 = {
            //     //     impactPointsEarned: 1610,
            //     //     lock: false,
            //     //     landscapeId: '67f433d9faee081450edc59d',
            //     //     chainName: 'optimism',
            //     //     contractAddress: '0x03b4bb1b349494dd6Dc47ECA50141E7b9748F0ce',
            //     //     chainId: 10,
            //     //     heldTokenId: 0,
            //     //     uid: 'RVs9zwwUxCM58Ejzv1BsctB6ORt1',
            //     //     walletAddress: '0xfb1e934e0bc827ef139caac4d7570624ed7a4e3b',
            //     //     platform: 'IMPACT_FOUNDRY',
            //     //     active: true,
            //     //     id: '684bfd322c5f9208eeb750b2',
            //     // };
            //     // const pill2 = {
            //     //     tokenId: response?.chainName === 'solana' ? response?.assetId : response?.heldTokenId,
            //     //     imageURI: primaryPillTransferred?.image,
            //     //     chainName: response?.chainName,
            //     //     collectionAddress: 'Xf5AwV2AJnKviKt54Ny5iMEg8ttshhNopUi3sJ4Woyc',
            //     //     collectionNo: 10,
            //     //     nftProgress: {
            //     //         colour: primaryPillTransferred?.colour,
            //     //         image: primaryPillTransferred?.image,
            //     //         progress: primaryPillTransferred?.progress,
            //     //         nftEvolutionThreshold: primaryPillTransferred?.nftEvolutionThreshold,
            //     //     },
            //     //     name: 'Malay Archipelago',
            //     //     chainImage: 'https://atlantis-testing-bucket.s3.ap-south-1.amazonaws.com/solana-sol-logo.png',
            //     //     landscapeId: '67f443e072efff145ea6f4a7',
            //     // };
            // }

            dispatch({
                type: 'home/setSelectedPill',
                payload: found ? { ...found, nftId: response?.id } : found,
            });
        } catch (error) {
            return null;
        }
    };
    const putSelectedPill = async (payload) => {
        try {
            const { chainName, contractAddress, assetId, landscapeId, collectionAddress, chainId, tokenId } = payload;
            const createPayload = {
                landscapeId: landscapeId,
                chainName: chainName,
                ...(collectionAddress
                    ? { collectionAddress: collectionAddress, assetId: tokenId }
                    : { contractAddress: contractAddress, chainId: chainId, heldTokenId: tokenId }),
            };
            const response = await setPrimaryPill(createPayload);

            const found = state?.greenPillNFTsHoldByWalletAddress?.find(
                ({ chainName, contractAddress, collectionAddress, tokenId }) =>
                    chainName + (chainName === 'solana' ? collectionAddress : contractAddress) + tokenId ===
                    response?.chainName +
                        (response?.chainName === 'solana' ? response?.collectionAddress : response?.contractAddress) +
                        (response?.chainName === 'solana' ? response?.assetId : response?.heldTokenId)
            );

            dispatch({
                type: 'home/setSelectedPill',
                payload: found ? { ...found, nftId: response?.id } : found,
            });
            // dispatch({
            //     type: 'home/setSelectedPill',
            //     payload: payload,
            // });
        } catch (error) {
            return null;
        }
    };
    useEffect(
        function () {
            if (landscapesState?.landscapes?.length > 0) fetchCounts();
            if (landscapesState?.landscapes?.length > 0 && (account || solanaAccount)) fetchData();

            // if (landscapesState?.landscapes.length > 0) fetchCounts();
        },
        [landscapesState?.landscapes, account, solanaAccount]
    );
    useEffect(
        function () {
            if (state?.greenPillNFTsHoldByWalletAddress.length > 0) fetchSelectedPill();
        },
        [state?.greenPillNFTsHoldByWalletAddress]
    );

    return (
        <HomeContext.Provider value={{ state, dispatch, loading, gettingGreenpillCounts, fetchData, putSelectedPill }}>
            {children}
        </HomeContext.Provider>
    );
};

const useHome = () => {
    return useContext(HomeContext);
};

export { HomeProvider, useHome };
